import { useState, useEffect } from 'react'

import {
    Chart,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartSeriesLabels,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartValueAxisLabels,
    ChartTitle,
} from '@progress/kendo-react-charts'
import { TextField } from '@material-ui/core'
import { MoneyInput, ToggleButtons } from '../../../GlobalComponents/Inputs/Inputs'

import styles from '../../TechPartnerDashboard/TechPartnerDashboard.module.scss'
import { COLORS } from '../../../consts'

import {
    fetchTechPartnerPlan,
    fetchTechPartnerSales,
    MonthlySalesData,
    TechPartnerSalesResults,
    fetchTechPartnerCAGR,
    CAGRData,
    fetchTechPartnerSalesQuota,
    YearlySalesQuotaData,
} from '../../../features/sales/salesAPI'
import { TechPartnerDashboardDataSectionProps } from '../TechPartnerDashboardSection'
import { getIconClass } from '../../../FastQuote/Components/OrderScreen'
import { HoverIcon } from '../../../GlobalComponents/_icons/HoverIcon'

export const SalesQuotaData = ({
    selectedInputs,
    selectedSalespeople,
    selectedSalesDivisions,
    loading,
    onLoadingChange,
}: TechPartnerDashboardDataSectionProps) => {
    enum VIEW_BY_TYPE {
        'Report Period' = 0,
        'Calendar Year' = 1,
    }

    const [planDataPayload, setPlanDataPayload] = useState<Array<MonthlySalesData>>()
    const [salesDataPayload, setSalesDataPayload] = useState<TechPartnerSalesResults>()
    const [salesQuotaDataPayload, setSalesQuotaDataPayload] =
        useState<Array<YearlySalesQuotaData>>()

    const [viewByType, setViewByType] = useState<VIEW_BY_TYPE>(VIEW_BY_TYPE['Calendar Year'])
    const [hasPOSReport, setHasPOSReport] = useState<boolean | null | undefined>(undefined)

    const [quotaYTDReportPeriod, setQuotaYTDReportPeriod] = useState<number>(0)
    const [quotaYTDCalendar, setQuotaYTDCalendar] = useState<number>(0)
    const [quotaFullYearReportPeriod, setQuotaFullYearReportPeriod] = useState<number>(0)
    const [quotaFullYearCalendar, setQuotaFullYearCalendar] = useState<number>(0)
    const [planFullYearReportPeriod, setPlanFullYearReportPeriod] = useState<number>(0)
    const [planFullYearCalendar, setPlanFullYearCalendar] = useState<number>(0)
    const [planYTDReportPeriod, setPlanYTDReportPeriod] = useState<number>(0)
    const [planYTDCalendar, setPlanYTDCalendar] = useState<number>(0)
    const [salesYTDReportPeriod, setSalesYTDReportPeriod] = useState<number>(0)
    const [salesYTDCalendar, setSalesYTDCalendar] = useState<number>(0)
    const [salesPYTDReportPeriod, setSalesPYTDReportPeriod] = useState<number>(0)
    const [salesPYTDCalendar, setSalesPYTDCalendar] = useState<number>(0)
    const [actualPercentGrowthReportPeriod, setActualPercentGrowthReportPeriod] =
        useState<number>(0)
    const [actualPercentGrowthCalendar, setActualPercentGrowthCalendar] = useState<number>(0)
    const [totalPercentGrowth, setTotalPercentGrowth] = useState<number>(0)
    const [quotaPercentGrowth, setQuotaPercentGrowth] = useState<number>(0)
    const [inflationPercentGrowth, setInflationPercentGrowth] = useState<number>(0)
    const [deltaPercentGrowth, setDeltaPercentGrowth] = useState<number>(0)

    const [salesQuotaExistsForAllSalespeople, setSalesQuotaExistsForAllSalespeople] =
        useState<boolean>(true)
    const [refreshChart, setRefreshChart] = useState<boolean>(true)

    const [cagrDataPayload, setCAGRDataPayload] = useState<CAGRData>()

    const [P5YSales, setP5YSales] = useState<number>(0)
    const [P4YSales, setP4YSales] = useState<number>(0)
    const [P3YSales, setP3YSales] = useState<number>(0)
    const [P2YSales, setP2YSales] = useState<number>(0)
    const [PYSales, setPYSales] = useState<number>(0)
    const [CYSales, setCYSales] = useState<number>(0)
    const [CYGoal, setCYGoal] = useState<number>(0)
    const [CYRunRate, setCYRunRate] = useState<number>(0)
    const [P3YSalesGrowthRate, setP3YSalesGrowthRate] = useState<number>(0)
    const [P2YSalesGrowthRate, setP2YSalesGrowthRate] = useState<number>(0)
    const [PYSalesGrowthRate, setPYSalesGrowthRate] = useState<number>(0)
    const [CYRunRateGrowthRate, setCYRunRateGrowthRate] = useState<number>(0)
    const [P3YearCAGR, setP3YearCAGR] = useState<number>(0)
    const [P5YearCAGR, setP5YearCAGR] = useState<number>(0)

    useEffect(() => {
        if (Object.keys(selectedInputs).length === 0) return

        onLoadingChange(true)

        let dataSales: TechPartnerSalesResults
        let dataPlan: Array<MonthlySalesData>
        let dataSalesQuota: Array<YearlySalesQuotaData>
        let dataCAGR: CAGRData

        async function getTechPartnerPlan() {
            if (Object.keys(selectedInputs).length === 0) return

            dataPlan = await fetchTechPartnerPlan(selectedInputs)
        }

        async function getTechPartnerSales() {
            if (Object.keys(selectedInputs).length === 0) return

            dataSales = await fetchTechPartnerSales(selectedInputs)
        }

        async function getTechPartnerSalesQuota() {
            if (Object.keys(selectedInputs).length === 0) return

            dataSalesQuota = await fetchTechPartnerSalesQuota(selectedInputs)
        }

        async function getTechPartnerCAGR() {
            if (Object.keys(selectedInputs).length === 0) return

            dataCAGR = await fetchTechPartnerCAGR(selectedInputs)
        }

        // run get functions in parallel, but for both to finish to avoid extra renders
        const asyncFunctions = [
            getTechPartnerPlan,
            getTechPartnerSales,
            getTechPartnerSalesQuota,
            getTechPartnerCAGR,
        ]
        const getData = async () => {
            await Promise.all(
                asyncFunctions.map(async (get) => {
                    await get()
                })
            )
        }

        getData().then(() => {
            if (dataPlan) setPlanDataPayload(dataPlan)
            if (dataSales) setSalesDataPayload(dataSales)
            if (dataSalesQuota) setSalesQuotaDataPayload(dataSalesQuota)
            if (dataCAGR) setCAGRDataPayload(dataCAGR)
        })

        setRefreshChart(true)
    }, [selectedInputs])

    useEffect(() => {
        if (Object.keys(selectedInputs).length > 0) calculateSalesQuota()

        onLoadingChange(false)
    }, [
        planDataPayload,
        salesDataPayload,
        salesQuotaDataPayload,
        cagrDataPayload,
        quotaYTDReportPeriod,
        quotaFullYearReportPeriod,
        quotaPercentGrowth,
        inflationPercentGrowth,
    ])

    const calculateSalesQuota = () => {
        const today = new Date()

        const ReportPeriodBeginYear = selectedInputs.ReportPeriodBegin
            ? selectedInputs.ReportPeriodBegin.getFullYear()
            : today.getFullYear()
        const ReportPeriodBeginMonth = selectedInputs.ReportPeriodBegin
            ? selectedInputs.ReportPeriodBegin.getMonth()
            : 0
        const ReportPeriodEndYear = selectedInputs.ReportPeriodEnd
            ? selectedInputs.ReportPeriodEnd.getFullYear()
            : today.getFullYear()
        const ReportPeriodEndMonth = selectedInputs.ReportPeriodEnd
            ? selectedInputs.ReportPeriodEnd.getMonth()
            : 0

        const CurrentMonth = today.getMonth()

        const MonthDiff =
            ReportPeriodBeginMonth +
            12 * ReportPeriodBeginYear -
            (ReportPeriodEndMonth + 12 * ReportPeriodEndYear)

        const daysInMonth = (year: number, month: number) => {
            return new Date(year, month, 0).getDate()
        }
        let fractionOfTheMonth =
            (today.getDate() - 1) / daysInMonth(today.getFullYear(), today.getMonth() + 1)
        // Round up to entire month if > 89% of month completed... allows for close outs when prior to month end
        if (fractionOfTheMonth > 0.89) fractionOfTheMonth = 1

        if (planDataPayload) {
            let dPlanFYReportPeriod = planDataPayload
                .filter((month) => month.MonthAge >= MonthDiff && month.MonthAge < MonthDiff + 12)
                .reduce((a, b) => a + b.Sales, 0)
            if (!dPlanFYReportPeriod) dPlanFYReportPeriod = 0
            setPlanFullYearReportPeriod(dPlanFYReportPeriod)

            let dPlanFYCalendar = planDataPayload
                .filter(
                    (month) =>
                        month.MonthAge >= -CurrentMonth && month.MonthAge <= CurrentMonth + 12
                )
                .reduce((a, b) => a + b.Sales, 0)
            if (!dPlanFYCalendar) dPlanFYCalendar = 0
            setPlanFullYearCalendar(dPlanFYCalendar)

            let dPlanYTDReportPeriod = planDataPayload
                .filter((month) => month.MonthAge >= MonthDiff && month.MonthAge <= 0)
                .reduce(
                    (a, b) => a + (b.MonthAge !== 0 ? b.Sales : b.Sales * fractionOfTheMonth),
                    0
                )
            if (!dPlanYTDReportPeriod) dPlanYTDReportPeriod = 0
            setPlanYTDReportPeriod(dPlanYTDReportPeriod)

            let dPlanYTDCalendar = planDataPayload
                .filter((month) => month.MonthAge >= -CurrentMonth && month.MonthAge <= 0)
                .reduce(
                    (a, b) => a + (b.MonthAge !== 0 ? b.Sales : b.Sales * fractionOfTheMonth),
                    0
                )
            if (!dPlanYTDCalendar) dPlanYTDCalendar = 0
            setPlanYTDCalendar(dPlanYTDCalendar)
        }

        if (salesDataPayload) {
            let dSalesYTDReportPeriod = salesDataPayload.ReportPeriodSalesYTD
            if (!dSalesYTDReportPeriod) dSalesYTDReportPeriod = 0
            setSalesYTDReportPeriod(dSalesYTDReportPeriod)

            let dSalesYTDCalendar = salesDataPayload.CalendarYearSalesYTD
            if (!dSalesYTDCalendar) dSalesYTDCalendar = 0
            setSalesYTDCalendar(dSalesYTDCalendar)

            let dSalesPYTDReportPeriod = salesDataPayload.ReportPeriodSalesPYTD
            if (!dSalesPYTDReportPeriod) dSalesPYTDReportPeriod = 0
            setSalesPYTDReportPeriod(dSalesPYTDReportPeriod)

            let dSalesPYTDCalendar = salesDataPayload.CalendarYearSalesPYTD
            if (!dSalesPYTDCalendar) dSalesPYTDCalendar = 0
            setSalesPYTDCalendar(dSalesPYTDCalendar)

            if (!dSalesYTDReportPeriod) dSalesYTDReportPeriod = 0
            if (dSalesPYTDReportPeriod && dSalesPYTDReportPeriod !== 0) {
                setActualPercentGrowthReportPeriod(
                    dSalesYTDReportPeriod / dSalesPYTDReportPeriod - 1
                )
                setDeltaPercentGrowth(
                    totalPercentGrowth === 0
                        ? 0
                        : (dSalesYTDReportPeriod / dSalesPYTDReportPeriod - 1) / totalPercentGrowth
                )
            }

            if (dSalesYTDCalendar && dSalesPYTDCalendar !== 0 && dSalesPYTDCalendar)
                setActualPercentGrowthCalendar(dSalesYTDCalendar / dSalesPYTDCalendar - 1)
        }

        if (salesQuotaDataPayload) {
            const selectedVendorIDs = [
                ...new Set(selectedInputs.VendorProductCodes?.map((vpc) => vpc.VendorID)),
            ]

            let reportPeriodBegin = selectedInputs.ReportPeriodBegin!
            let reportPeriodEnd = selectedInputs.ReportPeriodEnd!
            const fractionOfTheYearSoFarMonths = (reportPeriodEnd.getMonth() + 1) / 12
            const fractionOfTheYearLeftMonths = 1 - reportPeriodBegin.getMonth() / 12

            const reportPeriodBeginYear = reportPeriodBegin.getFullYear()
            const reportPeriodEndYear = selectedInputs.ReportPeriodEnd!.getFullYear()

            let dSalesQuotaFullYearReportPeriod = salesQuotaDataPayload
                ?.filter(
                    (quota) =>
                        (selectedVendorIDs.length === 0 ||
                            selectedVendorIDs.find((v) => v === quota.VendorID) !== undefined) &&
                        quota.Year === reportPeriodBeginYear
                )
                .reduce((a, b) => a + b.Quota * fractionOfTheYearSoFarMonths, 0)
            if (!dSalesQuotaFullYearReportPeriod) dSalesQuotaFullYearReportPeriod = 0

            if (reportPeriodBeginYear !== reportPeriodEndYear) {
                let dSalesQuotaFullYearReportPeriodEnd = salesQuotaDataPayload
                    ?.filter(
                        (quota) =>
                            (selectedVendorIDs.length === 0 ||
                                selectedVendorIDs.find((v) => v === quota.VendorID) !==
                                    undefined) &&
                            quota.Year === reportPeriodEndYear
                    )
                    .reduce((a, b) => a + b.Quota * fractionOfTheYearLeftMonths, 0)
                if (!dSalesQuotaFullYearReportPeriodEnd) dSalesQuotaFullYearReportPeriodEnd = 0

                dSalesQuotaFullYearReportPeriod += dSalesQuotaFullYearReportPeriodEnd
            }

            setQuotaFullYearReportPeriod(dSalesQuotaFullYearReportPeriod)

            const daysIntoYear = (date: Date) => {
                return (
                    (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
                        Date.UTC(date.getFullYear(), 0, 0)) /
                    24 /
                    60 /
                    60 /
                    1000
                )
            }

            let daysInYear = daysIntoYear(new Date(reportPeriodBegin.getFullYear(), 11, 31))
            let fractionOfTheYearToReportPeriodBeginDays =
                (daysIntoYear(new Date(reportPeriodBegin)) - 1) / daysInYear

            let endDay =
                reportPeriodBeginYear === reportPeriodEndYear
                    ? today
                    : new Date(reportPeriodBeginYear, 11, 31)

            let fractionOfTheYearToEndDayDays = (daysIntoYear(new Date(endDay)) - 1) / daysInYear
            let fractionOfTheReportPeriodSoFarDays =
                fractionOfTheYearToEndDayDays - fractionOfTheYearToReportPeriodBeginDays

            let dSalesQuotaYTDReportPeriod = salesQuotaDataPayload
                ?.filter(
                    (quota) =>
                        (selectedVendorIDs.length === 0 ||
                            selectedVendorIDs.find((v) => v === quota.VendorID) !== undefined) &&
                        quota.Year === reportPeriodBeginYear
                )
                .reduce((a, b) => a + b.Quota * fractionOfTheReportPeriodSoFarDays, 0)
            if (!dSalesQuotaYTDReportPeriod) dSalesQuotaYTDReportPeriod = 0

            if (reportPeriodBeginYear !== reportPeriodEndYear) {
                const fractionOfTheYearToTodayDays =
                    (daysIntoYear(new Date(today)) - 1) / daysInYear
                let dSalesQuotaYTDReportPeriodEnd = salesQuotaDataPayload
                    ?.filter(
                        (quota) =>
                            (selectedVendorIDs.length === 0 ||
                                selectedVendorIDs.find((v) => v === quota.VendorID) !==
                                    undefined) &&
                            quota.Year === reportPeriodEndYear
                    )
                    .reduce((a, b) => a + b.Quota * fractionOfTheYearToTodayDays, 0)
                if (!dSalesQuotaYTDReportPeriodEnd) dSalesQuotaYTDReportPeriodEnd = 0

                dSalesQuotaYTDReportPeriod += dSalesQuotaYTDReportPeriodEnd
            }

            setQuotaYTDReportPeriod(dSalesQuotaYTDReportPeriod)

            const calendarYearBegin = new Date(`1/1/${today.getFullYear()}`)
            let dSalesQuotaFullYearCalendar = salesQuotaDataPayload
                ?.filter(
                    (quota) =>
                        (selectedVendorIDs.length === 0 ||
                            selectedVendorIDs.find((v) => v === quota.VendorID) !== undefined) &&
                        quota.Year === calendarYearBegin.getFullYear()
                )
                .reduce((a, b) => a + b.Quota, 0)
            if (!dSalesQuotaFullYearCalendar) dSalesQuotaFullYearCalendar = 0

            setQuotaFullYearCalendar(dSalesQuotaFullYearCalendar)

            daysInYear = daysIntoYear(new Date(calendarYearBegin.getFullYear(), 11, 31))

            let fractionOfTheYearToTodayDays = (daysIntoYear(new Date(today)) - 1) / daysInYear

            let dSalesQuotaYTDCalendar = salesQuotaDataPayload
                ?.filter(
                    (quota) =>
                        (selectedVendorIDs.length === 0 ||
                            selectedVendorIDs.find((v) => v === quota.VendorID) !== undefined) &&
                        quota.Year === today.getFullYear()
                )
                .reduce((a, b) => a + b.Quota * fractionOfTheYearToTodayDays, 0)
            if (!dSalesQuotaYTDCalendar) dSalesQuotaYTDCalendar = 0

            setQuotaYTDCalendar(dSalesQuotaYTDCalendar)

            const selectedSalespersonCodes = [
                ...new Set(selectedSalespeople?.map((spc) => spc.SalespersonCode.substring(0, 3))),
            ]
            const salespersonCodesWithQuotaData = [
                ...new Set(
                    salesQuotaDataPayload
                        .filter((s) => s.QuotaBy === 3)
                        .map((sq) => sq.QuotaAssignedTo)
                ),
            ]

            const salespersonCodesMissingQuotaData = selectedSalespersonCodes.filter(
                (sp) => !salespersonCodesWithQuotaData.includes(sp)
            )

            setSalesQuotaExistsForAllSalespeople(salespersonCodesMissingQuotaData.length === 0)
        }

        if (cagrDataPayload) {
            setP5YSales(cagrDataPayload.P5YSales)
            setP4YSales(cagrDataPayload.P4YSales)
            setP3YSales(cagrDataPayload.P3YSales)
            setP2YSales(cagrDataPayload.P2YSales)
            setPYSales(cagrDataPayload.PYSales)
            setCYSales(cagrDataPayload.CYSales)
            setCYRunRate(cagrDataPayload.CYRunRate)
            setP3YSalesGrowthRate(cagrDataPayload.P3YSalesGrowthRate)
            setP2YSalesGrowthRate(cagrDataPayload.P2YSalesGrowthRate)
            setPYSalesGrowthRate(cagrDataPayload.PYSalesGrowthRate)
            setCYRunRateGrowthRate(cagrDataPayload.CYRunRateGrowthRate)
            setP3YearCAGR(cagrDataPayload.P3YearCAGR)
            setP5YearCAGR(cagrDataPayload.P5YearCAGR)

            setHasPOSReport(cagrDataPayload.POSReportPresent)
        }
    }

    const quotaYTD = () => {
        return viewByType === VIEW_BY_TYPE['Report Period']
            ? quotaYTDReportPeriod
            : quotaYTDCalendar
    }

    const planYTD = () => {
        return viewByType === VIEW_BY_TYPE['Report Period'] ? planYTDReportPeriod : planYTDCalendar
    }

    const salesYTD = () => {
        return viewByType === VIEW_BY_TYPE['Report Period']
            ? salesYTDReportPeriod
            : salesYTDCalendar
    }

    const salesPYTD = () => {
        return viewByType === VIEW_BY_TYPE['Report Period']
            ? salesPYTDReportPeriod
            : salesPYTDCalendar
    }

    const quotaFullYear = () => {
        return viewByType === VIEW_BY_TYPE['Report Period']
            ? quotaFullYearReportPeriod
            : quotaFullYearCalendar
    }

    const actualGrowthPercent = () => {
        return viewByType === VIEW_BY_TYPE['Report Period']
            ? actualPercentGrowthReportPeriod
            : actualPercentGrowthCalendar
    }

    const planFullYear = () => {
        return viewByType === VIEW_BY_TYPE['Report Period']
            ? planFullYearReportPeriod
            : planFullYearCalendar
    }

    const barColor: string = viewByType === VIEW_BY_TYPE['Report Period'] ? '#98C27B' : '#EFA16B'

    interface graphData {
        name: string
        sales: number
        color: string
        labelExtra?: string
    }

    const quotaSeriesData: graphData[] = [
        {
            name: 'TP Plan\nYTD',
            sales: quotaYTD(),
            color: barColor,
        },
        {
            name: 'AWC\nPlan YTD',
            sales: planYTD(),
            color: barColor,
        },
        {
            name: 'AWC Sales\nYTD',
            sales: salesYTD(),
            color: barColor,
        },
        {
            name: 'AWC Sales\nPYTD',
            sales: salesPYTD(),
            color: barColor,
        },
        {
            name: 'TP Plan\nFull Year',
            sales: quotaFullYear(),
            color: barColor,
        },
        {
            name: 'AWC Plan\nFull Year',
            sales: planFullYear(),
            color: barColor,
        },
    ]

    const lineColor: string = '#4472C4'
    const today = new Date()
    const cagrSeriesData: graphData[] = [
        {
            name: (today.getFullYear() - 5).toString(),
            sales: P5YSales,
            color: lineColor,
        },
        {
            name: (today.getFullYear() - 4).toString(),
            sales: P4YSales,
            color: lineColor,
        },
        {
            name: `${today.getFullYear() - 3}\n&nbsp;\n${P3YSalesGrowthRate.toLocaleString(
                'en-US',
                {
                    style: 'percent',
                    maximumFractionDigits: 1,
                }
            )}`,
            sales: P3YSales,
            color: lineColor,
        },
        {
            name: `${today.getFullYear() - 2}\n&nbsp;\n${P2YSalesGrowthRate.toLocaleString(
                'en-US',
                {
                    style: 'percent',
                    maximumFractionDigits: 1,
                }
            )}`,
            sales: P2YSales,
            color: lineColor,
        },
        {
            name: `${today.getFullYear() - 1}\n&nbsp;\n${PYSalesGrowthRate.toLocaleString('en-US', {
                style: 'percent',
                maximumFractionDigits: 1,
            })}`,
            sales: PYSales,
            color: lineColor,
        },
        {
            name: today.getFullYear().toString(),
            sales: CYRunRate,
            color: lineColor,
            labelExtra: 'RR',
        },
        // {
        //     name: `${today.getFullYear()}\nGoal`,
        //     sales: CYGoal,
        //     color: lineColor,
        // },
    ]

    const handleChartRefresh = (chartOptions: any, themeOptions: any, chartInstance: any) => {
        if (refreshChart) chartInstance.setOptions(chartOptions, themeOptions)
    }

    const POSReportIcon = () => {
        let title = 'POS report does not exist for selected tech partner'
        let className = getIconClass(COLORS.GREY)
        let marginLeft = '0px'
        let marginTop = '-2px'
        if (hasPOSReport === true) {
            className = getIconClass(COLORS.GREEN)
            title = 'POS report exists for selected tech partner'
            marginTop = '0px'
        } else if (hasPOSReport === null) {
            className = getIconClass(COLORS.YELLOW)
            title = 'Multiple tech partners have been selected'
            marginTop = '0px'
        }

        return (
            <div
                className={className}
                style={{ marginLeft: marginLeft, marginTop: marginTop }}
                title={title}
            />
        )
    }

    return (
        <div className={styles.salesQuota} id='sales-quota'>
            <div className={`${styles.dataContainer} ${styles.quotaData}`}>
                <div className={styles.dataRows}>
                    <div className={styles.viewBy}>
                        <label style={{ whiteSpace: 'nowrap' }}>View By:</label>
                        <ToggleButtons
                            buttonText={['Report Period', 'Calendar Year']}
                            selectedIndex={1}
                            onChange={(newSelectedIndex) => setViewByType(newSelectedIndex)}
                        />
                    </div>
                    <div className={styles.posReport}>
                        <label>POS Report:</label>
                        <POSReportIcon />
                    </div>
                    <div className={`${styles.quotaGraph} ${styles.chartContainer}`}>
                        <Chart
                            style={{ width: '100%' }}
                            transitions={!loading}
                            onRefresh={handleChartRefresh}
                        >
                            <ChartTitle
                                text={`${VIEW_BY_TYPE[viewByType]} Sales Plan`}
                                font='400 20px Roboto'
                            />
                            <ChartValueAxis>
                                <ChartValueAxisItem majorGridLines={{ visible: false }}>
                                    <ChartValueAxisLabels format='c0' />
                                </ChartValueAxisItem>
                            </ChartValueAxis>
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem majorGridLines={{ visible: false }} />
                            </ChartCategoryAxis>
                            <ChartLegend visible={false} />
                            <ChartSeries>
                                <ChartSeriesItem
                                    type='column'
                                    gap={1} // area between categories - larger area -> thinner bars
                                    //spacing={1.5} // area between bars within categories - larger area -> thinner bars
                                    data={quotaSeriesData}
                                    categoryField='name'
                                    field='sales'
                                    colorField='color'
                                >
                                    <ChartSeriesLabels
                                        format='c0'
                                        position='outsideEnd'
                                        background='transparent'
                                    />
                                </ChartSeriesItem>
                            </ChartSeries>
                        </Chart>
                    </div>
                    <div className={`${styles.fiveYearSalesGraph} ${styles.chartContainer}`}>
                        <Chart
                            style={{ width: '100%' }}
                            transitions={!loading}
                            onRefresh={handleChartRefresh}
                        >
                            <ChartTitle text={'5 Year Sales'} font='400 16px Roboto' />
                            <ChartValueAxis>
                                <ChartValueAxisItem majorGridLines={{ visible: true }}>
                                    <ChartValueAxisLabels format='c0' />
                                </ChartValueAxisItem>
                            </ChartValueAxis>
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem majorGridLines={{ visible: false }} />
                            </ChartCategoryAxis>
                            <ChartLegend visible={false} />
                            <ChartSeries>
                                <ChartSeriesItem
                                    type='line'
                                    data={cagrSeriesData}
                                    categoryField='name'
                                    field='sales'
                                    colorField='color'
                                >
                                    <ChartSeriesLabels
                                        format='c0'
                                        background='transparent'
                                        content={(e): string => {
                                            let label = e.value.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0,
                                            })
                                            if (e.dataItem.labelExtra)
                                                label = `${label} (${e.dataItem.labelExtra})`

                                            return label
                                        }}
                                    />
                                </ChartSeriesItem>
                                <ChartSeriesItem
                                    type='line'
                                    data={[
                                        {
                                            name: today.getFullYear().toString(),
                                            sales: CYSales,
                                            color: lineColor,
                                            labelExtra: 'YTD',
                                        },
                                    ]}
                                    categoryField='name'
                                    field='sales'
                                    colorField='color'
                                >
                                    <ChartSeriesLabels
                                        format='c0'
                                        background='transparent'
                                        content={(e): string => {
                                            let label = e.value.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0,
                                            })
                                            if (e.dataItem.labelExtra)
                                                label = `${label} (${e.dataItem.labelExtra})`

                                            return label
                                        }}
                                    />
                                </ChartSeriesItem>
                            </ChartSeries>
                        </Chart>
                    </div>
                    <div className={styles.title}>{VIEW_BY_TYPE[viewByType]} Sales Plan</div>
                    <div className={styles.dataLabel}>Tech Partner Plan YTD</div>
                    <div className={styles.dataCurrency}>
                        {quotaYTD().toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataLabel}>AWC Plan YTD</div>
                    <div className={styles.dataCurrency}>
                        {planYTD().toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataLabel}>AWC Sales YTD</div>
                    <div className={styles.dataCurrency}>
                        {salesYTD().toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataLabel}>AWC Sales PYTD</div>
                    <div className={styles.dataCurrency}>
                        {salesPYTD().toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataLabel}>Tech Partner Plan Full Year</div>
                    <div className={styles.dataCurrency}>
                        {quotaFullYear().toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.dataLabel}>AWC Plan Full Year</div>
                    <div className={styles.dataCurrency}>
                        {planFullYear().toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                    <div className={styles.totalLabel}>Actual % Growth</div>
                    <div className={`${styles.dataPercent} ${styles.totalLabel}`}>
                        {actualGrowthPercent().toLocaleString('en-US', {
                            style: 'percent',
                            maximumFractionDigits: 1,
                        })}
                    </div>
                    <div>Plan % Growth</div>
                    <div>
                        <TextField
                            id='quotaPercentGrowth'
                            variant='outlined'
                            tabIndex={3}
                            onBlur={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)
                                setQuotaPercentGrowth(numVal / 100)
                                setTotalPercentGrowth(numVal / 100 + inflationPercentGrowth)
                                if (e.target.value !== '') e.target.value = `${e.target.value}%`
                                setRefreshChart(false)
                            }}
                            onFocus={(e) => e.target.select()}
                            className={styles.dataPercent}
                            style={{ marginLeft: 'auto', width: '30%' }}
                        />
                    </div>
                    <div>Inflation % Growth</div>
                    <div>
                        <TextField
                            id='inflationPercentGrowth'
                            variant='outlined'
                            tabIndex={4}
                            onBlur={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9./-]/g, '')
                                let numVal: number =
                                    e.target.value === '' ? 0 : parseFloat(e.target.value)
                                setInflationPercentGrowth(numVal / 100)
                                setTotalPercentGrowth(numVal / 100 + quotaPercentGrowth)
                                if (e.target.value !== '') e.target.value = `${e.target.value}%`
                                setRefreshChart(false)
                            }}
                            onFocus={(e) => e.target.select()}
                            className={styles.dataPercent}
                            style={{ marginLeft: 'auto', width: '30%' }}
                        />
                    </div>
                    <div></div>
                    <div></div>
                    <div className={styles.growthPercent}>Growth %</div>
                    <div className={`${styles.columnHeader} ${styles.cagrData}`}>
                        3 Year
                        <HoverIcon
                            growLeft={true}
                            growDown={false}
                            growUp={true}
                            renderIcon={() => <span>&nbsp;CAGR</span>}
                        >
                            {(closeBubble: () => void) => (
                                <div className={styles.identifierBubble} style={{ width: 270 }}>
                                    <div>
                                        When less than 3 years of information is available, the CAGR
                                        is adjusted accordingly.
                                    </div>
                                </div>
                            )}
                        </HoverIcon>
                    </div>
                    <div className={`${styles.columnHeader} ${styles.cagrData}`}>
                        5 Year
                        <HoverIcon
                            growLeft={true}
                            growDown={false}
                            growUp={true}
                            renderIcon={() => <span>&nbsp;CAGR</span>}
                        >
                            {(closeBubble: () => void) => (
                                <div className={styles.identifierBubble} style={{ width: 270 }}>
                                    <div>
                                        When less than 5 years of information is available, the CAGR
                                        is adjusted accordingly.
                                    </div>
                                </div>
                            )}
                        </HoverIcon>
                    </div>
                    <div></div>
                    <div className={styles.totalLabel}>Total % Growth</div>
                    <div className={`${styles.dataPercent} ${styles.totalLabel}`}>
                        {totalPercentGrowth.toLocaleString('en-US', {
                            style: 'percent',
                            minimumFractionDigits: 1,
                        })}
                    </div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div className={`${styles.dataPercent} ${styles.cagrData}`}>
                        {P3YearCAGR.toLocaleString('en-US', {
                            style: 'percent',
                            maximumFractionDigits: 2,
                        })}
                    </div>
                    <div className={`${styles.dataPercent} ${styles.cagrData}`}>
                        {P5YearCAGR.toLocaleString('en-US', {
                            style: 'percent',
                            maximumFractionDigits: 2,
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SalesQuotaData
